import { useAppDispatch, useAppSelector } from '../app/hooks';
import { toggleSidebar } from '../features/ui/uiSlice';
import { logOut } from '../features/auth/authSlice';
import { useLogoutMutation } from '../features/auth/authApi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef } from 'react';
import { useOutsideClick } from '../hooks/useOutsideClick';
import ClockButton from './Attendance/ClockButton';
import DashboardToggle from './Layout/DashboardToggle';

const EmployeeNavbar = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const user = useAppSelector((state) => state.auth.user);
    const [logoutApi] = useLogoutMutation();
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const profileRef = useRef<HTMLDivElement>(null);

    useOutsideClick(profileRef, () => {
        if (isProfileOpen) setIsProfileOpen(false);
    });

    const handleLogout = async () => {
        try {
            await logoutApi().unwrap();
            dispatch(logOut());
            navigate('/login');
        } catch (error) {
            console.error('Logout failed', error);
            dispatch(logOut());
            navigate('/login');
        }
    };

    const getGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return 'Good Morning';
        if (hour < 17) return 'Good Afternoon';
        return 'Good Evening';
    };

    const getPageTitle = () => {
        const path = location.pathname;
        if (path.includes('/ess/dashboard')) return 'Dashboard';
        if (path.includes('/ess/attendance')) return 'Attendance';
        if (path.includes('/ess/leaves')) return 'Time Off';
        if (path.includes('/ess/calendar')) return 'Calendar';
        if (path.includes('/ess/payslips')) return 'Payslips';
        if (path.includes('/ess/advance-request')) return 'Salary Advance';
        if (path.includes('/ess/profile')) return 'My Profile';
        if (path.includes('/ess/documents')) return 'Documents';
        if (path.includes('/ess/requests')) return 'Requests';
        return 'Employee Portal';
    };

    return (
        <header className="flex items-center justify-between px-6 lg:px-8 py-4 bg-white border-b border-gray-100 sticky top-0 z-40">
            <div className="flex items-center gap-4">
                <button
                    onClick={() => dispatch(toggleSidebar())}
                    className="text-gray-400 focus:outline-none lg:hidden hover:text-indigo-600 transition-colors"
                >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 6H20M4 12H20M4 18H11" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
                <div className="hidden sm:block">
                    <h1 className="text-lg font-bold text-gray-900 tracking-tight">
                        {getPageTitle()}
                    </h1>
                    <p className="text-xs text-gray-400 font-medium">
                        {getGreeting()}, {user?.firstName ?? 'there'}
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-3">

                {/* Global Clock Button */}
                <div className="hidden sm:flex items-center gap-3">
                    <DashboardToggle />
                    <ClockButton />
                </div>

                {/* Profile */}
                {user && (
                    <div className="relative" ref={profileRef}>
                        <button
                            onClick={() => setIsProfileOpen(!isProfileOpen)}
                            className="flex items-center gap-2 p-1 pl-1 pr-3 rounded-full hover:bg-gray-50 transition-colors focus:outline-none"
                        >
                            <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm border-2 border-white shadow-sm">
                                {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                            </div>
                            <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isProfileOpen && (
                            <div className="absolute right-0 mt-2 w-60 bg-white rounded-2xl shadow-2xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="px-4 py-3 border-b border-gray-50">
                                    <p className="text-sm font-bold text-gray-900">{user.firstName} {user.lastName}</p>
                                    <p className="text-xs text-gray-400 font-medium truncate">{user.email}</p>
                                    <span className="mt-2 inline-block text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full uppercase tracking-wider">
                                        Employee
                                    </span>
                                </div>
                                <div className="py-1">
                                    <button
                                        onClick={() => { navigate('/ess/profile'); setIsProfileOpen(false); }}
                                        className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                                    >
                                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                        My Profile
                                    </button>
                                    <button
                                        onClick={() => { navigate('/ess/documents'); setIsProfileOpen(false); }}
                                        className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
                                    >
                                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                        My Documents
                                    </button>
                                </div>
                                <div className="border-t border-gray-50 pt-1">
                                    <button
                                        onClick={handleLogout}
                                        className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-medium"
                                    >
                                        <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                                        Sign Out
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
};

export default EmployeeNavbar;
